
import React from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, Check, Clock, Bookmark, 
  ThumbsUp, ThumbsDown, MessageCircle, 
  Code, KeyRound, AlertCircle, ServerCrash,
  Webhook, Gauge, FileJson, Lock
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";

interface ApiReferenceContentProps {
  activePage: string;
  feedbackSubmitted: Record<string, boolean>;
  bookmarkedArticles: string[];
  submitFeedback: (articleId: string, isHelpful: boolean) => void;
  toggleBookmark: (articleId: string) => void;
}

export const ApiReferenceContent: React.FC<ApiReferenceContentProps> = ({
  activePage,
  feedbackSubmitted,
  bookmarkedArticles,
  submitFeedback,
  toggleBookmark,
}) => {
  if (activePage === "api-overview") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">API Overview</h1>
          <p className="text-gray-500 mt-2">
            Learn about the DGK Platform API and how to integrate it with your applications.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: July 15, 2023</span>
            <Badge variant="outline" className="ml-2">Developer</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="introduction">Introduction</h2>
          <p>
            The DGK Platform API provides programmatic access to the platform's core functionality, enabling developers to build custom 
            integrations and extend the platform's capabilities. Our RESTful API supports JSON formats and uses standard HTTP response 
            codes to indicate the success or failure of requests.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <KeyRound className="h-8 w-8 text-blue-500 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium mb-1">Authentication</h3>
                    <p className="text-sm text-gray-500">Secure your API requests using API keys or OAuth tokens.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <FileJson className="h-8 w-8 text-emerald-500 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium mb-1">Resources</h3>
                    <p className="text-sm text-gray-500">Access platform data and functionality through RESTful endpoints.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <Webhook className="h-8 w-8 text-purple-500 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium mb-1">Webhooks</h3>
                    <p className="text-sm text-gray-500">Receive real-time notifications for platform events.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <h2 id="base-url">Base URL</h2>
          <p>
            All API requests should be made to the following base URL:
          </p>
          
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm my-4">
            https://api.dgkplatform.com/v1
          </div>
          
          <p>
            Always use HTTPS to ensure your requests are encrypted during transit. The API version is included in the URL 
            to ensure backward compatibility as we evolve the API.
          </p>
          
          <h2 id="response-format">Response Format</h2>
          <p>
            All responses are returned in JSON format. A typical successful response looks like:
          </p>
          
          <pre className="bg-gray-50 p-4 rounded-lg border border-gray-200 overflow-x-auto text-sm my-4">
{`{
  "status": "success",
  "data": {
    // Response data
  },
  "meta": {
    "pagination": {
      "page": 1,
      "per_page": 20,
      "total": 100
    }
  }
}`}
          </pre>
          
          <p>
            Error responses include error details and HTTP status codes:
          </p>
          
          <pre className="bg-gray-50 p-4 rounded-lg border border-gray-200 overflow-x-auto text-sm my-4">
{`{
  "status": "error",
  "error": {
    "code": "invalid_request",
    "message": "The request was invalid",
    "details": [
      "Field 'name' is required"
    ]
  }
}`}
          </pre>

          <h2 id="api-clients">API Clients & SDKs</h2>
          <p>
            We provide official client libraries to simplify API integration:
          </p>
          
          <Tabs defaultValue="javascript" className="w-full my-6">
            <TabsList className="w-full">
              <TabsTrigger value="javascript" className="flex-1">JavaScript</TabsTrigger>
              <TabsTrigger value="python" className="flex-1">Python</TabsTrigger>
              <TabsTrigger value="ruby" className="flex-1">Ruby</TabsTrigger>
              <TabsTrigger value="php" className="flex-1">PHP</TabsTrigger>
            </TabsList>
            <TabsContent value="javascript" className="p-4 bg-gray-50 rounded-md mt-2">
              <h3 className="font-medium mb-2">JavaScript SDK</h3>
              <div className="space-y-3">
                <div className="font-mono text-sm">
                  npm install @dgk-platform/sdk
                </div>
                <pre className="bg-gray-100 p-3 rounded text-sm">
{`import { DGKClient } from '@dgk-platform/sdk';

const client = new DGKClient({
  apiKey: 'your_api_key'
});

// Get user profile
const profile = await client.users.getProfile();`}
                </pre>
              </div>
            </TabsContent>
            <TabsContent value="python" className="p-4 bg-gray-50 rounded-md mt-2">
              <h3 className="font-medium mb-2">Python SDK</h3>
              <div className="space-y-3">
                <div className="font-mono text-sm">
                  pip install dgk-platform
                </div>
                <pre className="bg-gray-100 p-3 rounded text-sm">
{`from dgk_platform import DGKClient

client = DGKClient(api_key='your_api_key')

# Get user profile
profile = client.users.get_profile()`}
                </pre>
              </div>
            </TabsContent>
            <TabsContent value="ruby" className="p-4 bg-gray-50 rounded-md mt-2">
              <h3 className="font-medium mb-2">Ruby SDK</h3>
              <div className="space-y-3">
                <div className="font-mono text-sm">
                  gem install dgk_platform
                </div>
                <pre className="bg-gray-100 p-3 rounded text-sm">
{`require 'dgk_platform'

client = DGKPlatform::Client.new(api_key: 'your_api_key')

# Get user profile
profile = client.users.get_profile`}
                </pre>
              </div>
            </TabsContent>
            <TabsContent value="php" className="p-4 bg-gray-50 rounded-md mt-2">
              <h3 className="font-medium mb-2">PHP SDK</h3>
              <div className="space-y-3">
                <div className="font-mono text-sm">
                  composer require dgk/platform-sdk
                </div>
                <pre className="bg-gray-100 p-3 rounded text-sm">
{`<?php
require_once 'vendor/autoload.php';

$client = new DGK\\Platform\\Client('your_api_key');

// Get user profile
$profile = $client->users->getProfile();`}
                </pre>
              </div>
            </TabsContent>
          </Tabs>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["api-overview"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("api-overview", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("api-overview", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("api-overview") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("api-overview")}
            >
              {bookmarkedArticles.includes("api-overview") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (activePage === "authentication") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">API Authentication</h1>
          <p className="text-gray-500 mt-2">
            Learn how to authenticate with the DGK Platform API to make secure requests.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: July 12, 2023</span>
            <Badge variant="outline" className="ml-2">Required</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="authentication-methods">Authentication Methods</h2>
          <p>
            The DGK Platform API supports the following authentication methods:
          </p>

          <div className="my-6 space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="text-lg font-medium mb-2 flex items-center">
                <KeyRound className="h-5 w-5 mr-2 text-blue-600" />
                API Key Authentication
              </h3>
              <p className="mb-4">
                The simplest way to authenticate is using API keys. Include your API key in the request header:
              </p>
              <pre className="bg-gray-100 p-3 rounded text-sm">
{`Authorization: Bearer YOUR_API_KEY`}
              </pre>
              <div className="bg-amber-50 p-3 rounded mt-4 flex items-start border border-amber-200">
                <AlertCircle className="h-5 w-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-amber-800 text-sm">
                  Treat your API keys like passwords. Never share them in public repositories or client-side code.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="text-lg font-medium mb-2 flex items-center">
                <Lock className="h-5 w-5 mr-2 text-green-600" />
                OAuth 2.0 Authentication
              </h3>
              <p className="mb-4">
                For user-specific actions, we recommend using OAuth 2.0 to obtain access tokens:
              </p>
              <ol className="list-decimal pl-5 space-y-2 mb-4">
                <li>Redirect users to our authorization URL</li>
                <li>User approves the requested permissions</li>
                <li>We redirect back to your app with an authorization code</li>
                <li>Exchange the authorization code for an access token</li>
                <li>Use the access token in your API requests</li>
              </ol>
              <div className="bg-blue-50 p-3 rounded flex items-start border border-blue-200">
                <Code className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-blue-800 text-sm">
                  See our <Link to="#" className="text-blue-600 underline">OAuth Integration Guide</Link> for detailed implementation steps.
                </p>
              </div>
            </div>
          </div>

          <h2 id="manage-api-keys">Managing API Keys</h2>
          <p>
            You can create and manage API keys from your account settings. Each API key can be scoped to specific permissions.
          </p>
          
          <div className="my-6">
            <h3 className="text-lg font-medium mb-3">API Key Best Practices</h3>
            <ul className="space-y-2">
              <li className="flex items-start">
                <Check className="h-4 w-4 text-emerald-500 mt-1 mr-2 flex-shrink-0" />
                <span>Create separate API keys for different applications or environments</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 text-emerald-500 mt-1 mr-2 flex-shrink-0" />
                <span>Use the principle of least privilege - only grant the permissions each key needs</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 text-emerald-500 mt-1 mr-2 flex-shrink-0" />
                <span>Rotate API keys regularly for enhanced security</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 text-emerald-500 mt-1 mr-2 flex-shrink-0" />
                <span>Store API keys securely in environment variables or secure credential stores</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 text-emerald-500 mt-1 mr-2 flex-shrink-0" />
                <span>Monitor API key usage for unauthorized access</span>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["authentication"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("authentication", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("authentication", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("authentication") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("authentication")}
            >
              {bookmarkedArticles.includes("authentication") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (activePage === "rate-limits") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">API Rate Limits</h1>
          <p className="text-gray-500 mt-2">
            Understanding rate limits and how to optimize your API usage.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: July 5, 2023</span>
            <Badge variant="outline" className="ml-2">Important</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="rate-limit-overview">Rate Limit Overview</h2>
          <p>
            To ensure fair usage and system stability, the DGK Platform API implements rate limits that restrict the number of requests
            you can make within a given time period. Rate limits vary by account tier and API endpoint.
          </p>

          <div className="my-6">
            <h3 className="text-lg font-medium mb-3">Rate Limit Headers</h3>
            <p className="mb-4">Each API response includes headers to help you track your rate limit usage:</p>
            
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Header</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-mono text-sm">X-RateLimit-Limit</TableCell>
                  <TableCell>Maximum number of requests allowed in the current time window</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-mono text-sm">X-RateLimit-Remaining</TableCell>
                  <TableCell>Number of requests remaining in the current time window</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-mono text-sm">X-RateLimit-Reset</TableCell>
                  <TableCell>Time in UTC epoch seconds when the rate limit window resets</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          <h2 id="rate-limit-tiers">Rate Limit Tiers</h2>
          <p>
            Different account tiers have different rate limit allocations:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-medium mb-2">Free Tier</h3>
                <p className="text-sm text-gray-500 mb-3">
                  Basic access with limited requests
                </p>
                <div className="flex justify-between items-center">
                  <Badge className="bg-gray-100 text-gray-800">60 requests/minute</Badge>
                  <Badge className="bg-gray-100 text-gray-800">10,000 requests/day</Badge>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-medium mb-2">Professional Tier</h3>
                <p className="text-sm text-gray-500 mb-3">
                  Enhanced access for professional use
                </p>
                <div className="flex justify-between items-center">
                  <Badge className="bg-blue-100 text-blue-800">300 requests/minute</Badge>
                  <Badge className="bg-blue-100 text-blue-800">100,000 requests/day</Badge>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-medium mb-2">Enterprise Tier</h3>
                <p className="text-sm text-gray-500 mb-3">
                  Maximum access for large-scale operations
                </p>
                <div className="flex justify-between items-center">
                  <Badge className="bg-purple-100 text-purple-800">1,000+ requests/minute</Badge>
                  <Badge className="bg-purple-100 text-purple-800">Custom daily limits</Badge>
                </div>
              </CardContent>
            </Card>
          </div>

          <h2 id="handling-rate-limits">Handling Rate Limits</h2>
          <p>
            When you exceed rate limits, the API returns a 429 Too Many Requests status code. Here's how to handle this situation:
          </p>
          
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 my-6">
            <h3 className="text-base font-medium mb-3">Best Practices</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Gauge className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Implement exponential backoff</strong>
                  <p className="text-sm text-gray-600 mt-1">
                    When you receive a 429 response, wait before retrying. Increase the wait time with each retry.
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <Code className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Use batch operations</strong>
                  <p className="text-sm text-gray-600 mt-1">
                    Combine multiple operations into a single API call when possible.
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <ServerCrash className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Monitor and track usage</strong>
                  <p className="text-sm text-gray-600 mt-1">
                    Use the rate limit headers to track your usage and adjust your request patterns accordingly.
                  </p>
                </div>
              </li>
            </ul>
          </div>

          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 my-6">
            <h3 className="text-base font-medium text-amber-800 mb-2 flex items-center">
              <AlertCircle className="h-5 w-5 mr-2" />
              Important Note
            </h3>
            <p className="text-amber-700 text-sm">
              Consistently exceeding your rate limits may result in temporary or permanent restrictions on your API access.
              If you need higher limits, consider upgrading your account or contacting our support team.
            </p>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["rate-limits"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("rate-limits", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("rate-limits", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("rate-limits") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("rate-limits")}
            >
              {bookmarkedArticles.includes("rate-limits") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
